import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Config from "./../config.json";

export const diseasesApi = createApi({
  reducerPath: "diseasesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${Config.IPM_SERVER_URL}/api/diseases/`,
    credentials: "include",
    headers: {
      "X-API-Key": Config.X_API_KEY,
    },
  }),
  endpoints: (builder) => ({
    getAllDiseases: builder.query({
      query: () => ({
        url: "getAll",
        params: {},
        method: "GET",
      }),
    }),
    getCropsDisease: builder.query({
      query: (id) => ({
        url: `getDiseases/${id}`,
        params: {},
        method: "GET",
      }),
      providesTags: (result) => [
        { type: "Disease", id: "List" },
        ...(result ?? []).map((Disease) => ({
          type: "Disease",
          id: Disease.id,
        })),
      ],
    }),
    createDisease: builder.mutation({
      query: (disease) => ({
        url: "create",
        body: disease,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "Disease", id: "List" },
        { type: "Disease", id: (id) => id },
      ],
    }),
    updateDisease: builder.mutation({
      query: (crop) => ({
        url: `updateDisease`,
        body: crop,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "Disease", id: "List" },
        { type: "Disease", id: (id) => id },
      ],
    }),
    deleteDisease: builder.mutation({
      query: (id) => ({
        url: `deleteDisease/${id}`,
        body: id,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Disease", id: "List" },
        { type: "Disease", id: (id) => id },
      ],
    }),
    deleteUploadedImage: builder.mutation({
      query: (id) => ({
        url: `deleteUploadedImage/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Disease", id: "List" },
        { type: "Disease", id: (id) => id },
      ],
    }),

    /// DiseaseInfoDescription
    createDiseaseInfoDescription: builder.mutation({
      query: (formData) => {
        return {
          url: "/createDescription",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [
        { type: "DiseaseInfoDescription", id: "List" },
        { type: "DiseaseInfoDescription", id: (id) => id },
      ],
    }),

    updateDiseaseInfoDescription: builder.mutation({
      query: (data) => ({
        url: `updateDescription`,
        body: data,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "DiseaseInfoDescription", id: "List" },
        { type: "DiseaseInfoDescription", id: (id) => id },
      ],
    }),
    deleteDiseaseInfoDescription: builder.mutation({
      query: (id) => ({
        url: `deleteDescription/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "DiseaseInfoDescription", id: "List" },
        { type: "DiseaseInfoDescription", id: (id) => id },
      ],
    }),
    onError: (error) => {
      console.error("An error occurred in the API:", error);
      // Handle the error here
    },
    deleteUploadedImageInDiseaseInfoDescription: builder.mutation({
      query: (id) => ({
        url: `deleteUploadedImage/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "DiseaseInfoDescription", id: "List" },
        { type: "DiseaseInfoDescription", id: (id) => id },
      ],
    }),
    onError: (error) => {
      console.error("An error occurred in the API:", error);
      // Handle the error here
    },
    getDiseaseInfoDescription: builder.query({
      query: (diseaseId) => ({
        url: `getDescription/${diseaseId}`,
        params: {},
        method: "GET",
      }),
      providesTags: (result) => [
        { type: "DiseaseInfoDescription", id: "List" },
        ...result?.map((DiseaseInfoDescription) => ({
          type: "DiseaseInfoDescription",
          id: DiseaseInfoDescription.id,
        })),
      ],
    }),

    // New Description
    getDiseaseDescription: builder.query({
      query: (dto) => ({
        url: `get-description/${dto.Id}/${dto.Type}`,
        params: {},
        method: "GET",
      }),
      providesTags: (result, error, dto) => [
        { type: "DiseaseDescriptionData", id: dto.Id },
      ],
    }),
    updateDiseaseDescription: builder.mutation({
      query: (dto) => ({
        url: `updated-description`,
        body: dto,
        method: "PUT",
      }),
      // invalidatesTags: [{ type: 'WeedDescription', id: 'List' },
      // { type: 'WeedDescriptionData', id: (id) => id }],
    }),

    // disease images
    // Weed Images
    getDiseaseImages: builder.query({
      query: (diseaseId) => ({
        url: `get-disease-images/${diseaseId}`,
        params: {},
        method: "GET",
      }),
      providesTags: (result) => [
        { type: "DiseaseImage", id: "List" },
        ...result?.map((DiseaseImage) => ({
          type: "DiseaseImage",
          id: DiseaseImage.id,
        })),
      ],
    }),
    updateImageOrder: builder.mutation({
      query: (data) => {
        return {
          url: `/update-image-order`,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: [
        { type: "DiseaseImage", id: "List" },
        { type: "DiseaseImage", id: (id) => id },
      ],
    }),
    createDiseaseImage: builder.mutation({
      query: (entity) => {
        return {
          url: `/upload-disease-image/${entity.Id}`,
          method: "POST",
          body: entity.imageFormData,
        };
      },
      invalidatesTags: [
        { type: "DiseaseImage", id: "List" },
        { type: "DiseaseImage", id: (id) => id },
      ],
    }),
    deleteDiseaseImage: builder.mutation({
      query: (image) => ({
        url: `/delete-disease-image`,
        body: image,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "DiseaseImage", id: "List" },
        { type: "DiseaseImage", id: (id) => id },
      ],
    }),
  }),
});

export const {
  useGetDiseaseDescriptionQuery,
  useUpdateDiseaseDescriptionMutation,
  useGetAllDiseasesQuery,
  useDeleteUploadedImageInDiseaseInfoDescriptionMutation,
  useDeleteUploadedImageMutation,
  useDeleteDiseaseInfoDescriptionMutation,
  useUpdateDiseaseInfoDescriptionMutation,
  useGetCropsDiseaseQuery,
  useCreateDiseaseMutation,
  useUpdateDiseaseMutation,
  useDeleteDiseaseMutation,
  useGetDiseaseInfoDescriptionQuery,
  useCreateDiseaseInfoDescriptionMutation,
  useGetDiseaseImagesQuery,
  useCreateDiseaseImageMutation,
  useDeleteDiseaseImageMutation,
  useUpdateImageOrderMutation
} = diseasesApi;
